import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useTheme, useMediaQuery } from '@mui/material';
import axiosInstance from '../AxiosInstance';
import axios from 'axios';
import RepeatIcon from '@mui/icons-material/Repeat';
import ProposalDayView from './ProposalDayView';
import ProposalSlotDetailDialog from './ProposalSlotDetailDialog';
import { formatLocalYmd, lastInclusiveYmdFromEndExclusive, normalizeCalendarDate } from './formatLocalYmd';
import { getEarliestManagerNewTargetDateYmdPst, isDateBeforeEarliestManagerNewTargetPst } from './managerTargetWindow';

const baseURL = process.env.NODE_ENV === 'production' ? 'https://allcprmanage.com' : 'http://localhost:8080';

/**
 * 共享排班日历组件
 * - Manager 模式: instructorId + managerId，使用认证 API
 * - Instructor 模式: token (base64 instructor_id)，使用公开 API
 */
const ProposalCalendar = ({
  instructorId,
  managerId,
  token,
  readOnly = false,
  onRangeChange,
  onEventsChange,
  onRefresh,
  refreshNonce,
  instructorAllowedMonthStart,
  instructorAllowedMonthEnd,
  instructorValidRangeEnd,
  managerInitialDate,
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentRange, setCurrentRange] = useState({ start: null, end: null });
  const [dayViewOpen, setDayViewOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slotDetailOpen, setSlotDetailOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isManagerMode = Boolean(instructorId && managerId);
  const isInstructorMode = Boolean(token);
  const isInstructorLockedMonth = isInstructorMode && instructorAllowedMonthStart && instructorAllowedMonthEnd;

  const fetchEvents = useCallback(async (start, end) => {
    if ((!isManagerMode && !isInstructorMode) || !start || !end) return;

    let rangeStart = start;
    let rangeEndExclusive = end;
    if (isInstructorLockedMonth && instructorAllowedMonthStart && instructorAllowedMonthEnd) {
      rangeStart = new Date(instructorAllowedMonthStart + 'T00:00:00');
      // First instant after last allowed day (exclusive end for [start, end) semantics)
      const p = instructorAllowedMonthEnd.split('-').map(Number);
      if (p.length === 3) {
        const [y, mo, day] = p;
        rangeEndExclusive = new Date(y, mo - 1, day + 1);
      } else {
        const rangeEndIncl = new Date(instructorAllowedMonthEnd + 'T23:59:59');
        rangeEndExclusive = new Date(rangeEndIncl);
        rangeEndExclusive.setDate(rangeEndExclusive.getDate() + 1);
      }
    }

    setLoading(true);
    setError(null);
    let monthStart = formatLocalYmd(rangeStart);
    let monthEnd = lastInclusiveYmdFromEndExclusive(rangeEndExclusive);
    if (!monthEnd && rangeEndExclusive instanceof Date && !Number.isNaN(rangeEndExclusive.getTime())) {
      const fallback = new Date(rangeEndExclusive.getTime());
      fallback.setDate(fallback.getDate() - 1);
      monthEnd = formatLocalYmd(fallback);
    }
    if (!monthStart || !monthEnd) {
      setLoading(false);
      setError('Invalid calendar range');
      return;
    }

    try {
      let response;
      if (isManagerMode) {
        response = await axiosInstance.get('/proposal/calendar', {
          params: { instructorId, managerId, monthStart, monthEnd },
        });
      } else {
        response = await axios.get(`${baseURL}/proposal/calendar/public`, {
          params: { token, monthStart, monthEnd },
        });
      }

      let scheduleList = [];
      try {
        if (isManagerMode) {
          const schRes = await axiosInstance.get('/instructor/schedule', { params: { instructorId } });
          scheduleList = schRes.data?.data || [];
        } else if (isInstructorMode) {
          const schRes = await axios.get(`${baseURL}/proposal/schedule/public`, { params: { token, monthStart, monthEnd } });
          scheduleList = schRes.data?.data || [];
        }
      } catch (e) {
        console.warn('Schedule fetch failed:', e);
      }

      const scheduleMap = new Map();
      scheduleList.forEach((s) => {
        const d = normalizeCalendarDate(s.date);
        const st = String(s.startTime || '').slice(0, 5);
        const et = String(s.endTime || '').slice(0, 5);
        if (!d || !st || !et) return;
        if (d < monthStart || d > monthEnd) return;
        scheduleMap.set(`${d}|${st}|${et}`, s);
      });

      const res = response.data;
      const data = Array.isArray(res?.data)
        ? res.data
        : res?.code !== undefined && res.code !== 0
          ? []
          : Array.isArray(res)
            ? res
            : res?.data || [];
      let eventsData = data
        .filter((ev) => {
          const d = normalizeCalendarDate(ev?.date);
          if (!d) return false;
          return d >= monthStart && d <= monthEnd;
        })
        .map((ev, idx) => {
        const dateYmd = normalizeCalendarDate(ev.date);
        const sourceType = ev.sourceType || 'INSTRUCTOR';
        const startMin = String(ev.startTime || '').slice(0, 5);
        const endMin = String(ev.endTime || '').slice(0, 5);
        const scheduleKey = `${dateYmd}|${startMin}|${endMin}`;
        const hasSchedule = scheduleMap.has(scheduleKey);
        const scheduleRow = hasSchedule ? scheduleMap.get(scheduleKey) : null;
        const isRejected = (ev.isRejected === true || ev.rejected === true) && (ev.isCancelled !== true && ev.cancelled !== true);
        let bgColor;
        if (hasSchedule) {
          bgColor = 'rgba(76, 175, 80, 0.5)';
        } else if (isRejected) {
          bgColor = 'rgba(244, 67, 54, 0.5)';
        } else if (sourceType === 'MANAGER') {
          bgColor = 'rgba(33, 150, 243, 0.5)';
        } else {
          bgColor = 'rgba(255, 193, 7, 0.5)';
        }
        const title = hasSchedule ? 'Schedule' : isRejected ? 'Rejected' : (sourceType === 'MANAGER' ? 'Target' : (ev.title || 'Available'));
        return {
          id: `${ev.id}-${dateYmd}-${ev.startTime}-${sourceType}-${idx}`,
          title,
          start: `${dateYmd}T${formatTime(ev.startTime)}`,
          end: ev.endTime ? `${dateYmd}T${formatTime(ev.endTime)}` : undefined,
          backgroundColor: bgColor,
          extendedProps: {
            startTime: ev.startTime,
            endTime: ev.endTime,
            sourceType,
            creatorName: ev.creatorName,
            proposalManagerId: ev.proposalManagerId,
            rawId: ev.id,
            parentId: ev.parentId,
            isRecurring: ev.isRecurring,
            rrule: ev.rrule,
            date: dateYmd,
            isSchedule: Boolean(hasSchedule),
            scheduleId: scheduleRow?.id,
            isRejected,
          },
        };
      });

      // Render confirmed schedule (instructorschedule) as green blocks for both manager and instructor views.
      if ((isManagerMode || isInstructorMode) && scheduleList.length > 0) {
        const keysFromProposals = new Set(
          eventsData.map((e) => {
            const d = e.start.slice(0, 10);
            const st = e.start.length >= 16 ? e.start.slice(11, 16) : '00:00';
            const et = (e.extendedProps?.endTime || '').toString().slice(0, 5);
            return `${d}|${st}|${et}`;
          })
        );
        scheduleList.forEach((s) => {
          const d = normalizeCalendarDate(s.date);
          const st = String(s.startTime || '').slice(0, 5);
          const et = String(s.endTime || '').slice(0, 5);
          if (!d || !st || !et || d < monthStart || d > monthEnd) return;
          const key = `${d}|${st}|${et}`;
          if (keysFromProposals.has(key)) return;
          keysFromProposals.add(key);
          const scheduleId = s.id;
          eventsData = eventsData.concat({
            id: `schedule-${scheduleId}-${d}-${st}-${et}`,
            title: 'Schedule',
            start: `${d}T${formatTime(s.startTime)}`,
            end: `${d}T${formatTime(s.endTime)}`,
            backgroundColor: 'rgba(76, 175, 80, 0.5)',
            extendedProps: {
              startTime: s.startTime,
              endTime: s.endTime,
              sourceType: 'MANAGER',
              creatorName: '',
              rawId: null,
              parentId: null,
              isRecurring: false,
              rrule: null,
              date: d,
              isSchedule: true,
              scheduleId,
              isRejected: false,
            },
          });
        });
      }

      // Exception rows often omit rrule/isRecurring; copy parent series flags for delete-scope UI.
      {
        const byRawId = new Map();
        eventsData.forEach((e) => {
          const id = e.extendedProps?.rawId;
          if (id != null) {
            byRawId.set(id, e);
            byRawId.set(String(id), e);
          }
        });
        eventsData = eventsData.map((e) => {
          const pid = e.extendedProps?.parentId;
          if (pid == null) return e;
          const parentEv = byRawId.get(pid) ?? byRawId.get(Number(pid));
          if (!parentEv?.extendedProps) return e;
          const pRrule = parentEv.extendedProps.rrule;
          const pRec = parentEv.extendedProps.isRecurring;
          if (!pRrule && !pRec) return e;
          return {
            ...e,
            extendedProps: {
              ...e.extendedProps,
              parentSeriesRrule: pRrule,
              parentSeriesIsRecurring: Boolean(pRec),
            },
          };
        });
      }

      setEvents(eventsData);
      onEventsChange && onEventsChange(eventsData);
    } catch (err) {
      console.error('Error fetching proposal calendar:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load calendar');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [instructorId, managerId, token, isManagerMode, isInstructorMode, isInstructorLockedMonth, instructorAllowedMonthStart, instructorAllowedMonthEnd, onEventsChange]);

  const formatTime = (t) => {
    if (!t) return '00:00:00';
    const s = String(t).trim();
    if (s.length >= 8) return s;
    return s.length <= 5 ? `${s}:00` : s;
  };

  const formatTimeToMinutes = (t) => {
    if (!t) return '00:00';
    if (t instanceof Date) {
      return `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
    }
    const s = String(t).trim();
    return s.length >= 5 ? s.slice(0, 5) : s;
  };

  const getStartTimeStr = (ev) => {
    if (!ev?.start) return '--:--';
    if (ev.start instanceof Date) return formatTimeToMinutes(ev.start);
    const s = String(ev.start);
    return formatTimeToMinutes(s.length >= 16 ? s.slice(11, 16) : s);
  };

  const getEndTimeStr = (ev) => {
    if (ev?.extendedProps?.endTime) return formatTimeToMinutes(ev.extendedProps.endTime);
    if (!ev?.end) return '--:--';
    if (ev.end instanceof Date) return formatTimeToMinutes(ev.end);
    const s = String(ev.end);
    return formatTimeToMinutes(s.length >= 16 ? s.slice(11, 16) : s);
  };

  const rangeRef = useRef({ start: null, end: null });
  const dragShadowIdRef = useRef(null);
  const rootRef = useRef(null);
  const handleDatesSet = useCallback((arg) => {
    const start = arg.view?.currentStart || arg.start;
    const end = arg.view?.currentEnd || arg.end;
    const prev = rangeRef.current;
    const sameRange = prev.start?.getTime() === start?.getTime() && prev.end?.getTime() === end?.getTime();
    if (!sameRange) {
      rangeRef.current = { start, end, view: arg.view };
      setCurrentRange({ start, end, view: arg.view });
      onRangeChange && onRangeChange({ start, end, view: arg.view });
      fetchEvents(start, end);
    }
  }, [onRangeChange, fetchEvents]);

  useEffect(() => {
    if (currentRange.start && currentRange.end) {
      fetchEvents(currentRange.start, currentRange.end);
    }
  }, [instructorId, managerId, token, refreshNonce]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const btn = el.querySelector('.fc-refresh-button');
    if (!btn) return;
    btn.setAttribute('title', 'Refresh calendar');
    btn.setAttribute('aria-label', 'Refresh calendar');
  });

  const recurringTooltip = (rrule) => {
    if (!rrule) return 'Recurring';
    const u = String(rrule).toUpperCase();
    if (u === 'DAILY') return 'Daily recurrence';
    if (u === 'WEEKLY') return 'Weekly recurrence';
    if (u === 'BIWEEKLY') return 'Biweekly recurrence';
    if (u === 'MONTHLY') return 'Monthly recurrence';
    return 'Recurring';
  };

  const renderEventContent = (eventInfo) => {
    const ev = eventInfo.event;
    const bgColor = ev.backgroundColor || 'rgba(255, 193, 7, 0.5)';
    const startStr = getStartTimeStr(ev);
    const endStr = getEndTimeStr(ev);
    const isRecurring = ev.extendedProps?.isRecurring === true && ev.extendedProps?.parentId == null;

    if (isMobile) {
      return (
        <div
          style={{
            backgroundColor: bgColor,
            padding: '2px 4px',
            borderRadius: 3,
            width: '100%',
            minHeight: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            lineHeight: 1.2,
          }}
        >
          <div>{startStr}</div>
          <div style={{ textAlign: 'center' }}>-</div>
          <div>{endStr}</div>
        </div>
      );
    }

    return (
      <div
        style={{
          backgroundColor: bgColor,
          padding: '2px 6px',
          borderRadius: 3,
          width: '100%',
          minHeight: '100%',
          boxSizing: 'border-box',
        }}
      >
        {(isManagerMode || isInstructorMode) && ev.extendedProps?.creatorName && (
          <div style={{ fontSize: 10, opacity: 0.9 }}>By: {ev.extendedProps.creatorName}</div>
        )}
        <div><b>s:</b> {startStr} <b>e:</b> {endStr}</div>
        <i>{ev.title}</i>
        {isRecurring && (
          <RepeatIcon
            sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', opacity: 0.8 }}
            titleAccess={recurringTooltip(ev.extendedProps?.rrule)}
          />
        )}
      </div>
    );
  };

  const handleDateClick = (arg) => {
    setSelectedDate(arg.dateStr);
    setDayViewOpen(true);
  };

  const handleEventClick = (arg) => {
    const ext = arg.event.extendedProps || {};
    // Ignore clicks on drag shadow events
    if (ext.isShadow) return;
    arg.jsEvent.preventDefault();
    setSelectedSlot(arg.event);
    setSlotDetailOpen(true);
  };

  const handleDayViewSlotClick = (ev) => {
    setSelectedSlot(ev);
    setSlotDetailOpen(true);
  };

  const fullCalendarInitialDate = useMemo(() => {
    if (isInstructorLockedMonth && instructorAllowedMonthStart) return instructorAllowedMonthStart + 'T00:00:00';
    if (isManagerMode && managerInitialDate) return managerInitialDate + 'T00:00:00';
    return undefined;
  }, [isInstructorLockedMonth, instructorAllowedMonthStart, isManagerMode, managerInitialDate]);
  const fullCalendarValidRange = useMemo(
    () => (isInstructorLockedMonth && instructorAllowedMonthStart && instructorValidRangeEnd
      ? { start: instructorAllowedMonthStart, end: instructorValidRangeEnd }
      : undefined),
    [isInstructorLockedMonth, instructorAllowedMonthStart, instructorValidRangeEnd]
  );

  const instructorHeaderToolbar = useMemo(
    () => (isInstructorMode && !isManagerMode
      ? { left: '', center: 'title', right: '' }
      : undefined),
    [isInstructorMode, isManagerMode]
  );

  const managerHeaderToolbar = useMemo(
    () => (isManagerMode
      ? { left: 'today,refresh', center: 'title', right: 'prev,next' }
      : undefined),
    [isManagerMode]
  );

  const headerToolbar = instructorHeaderToolbar || managerHeaderToolbar;

  const createDraggedTargetSlot = useCallback(async ({ date, startTime, endTime }) => {
    if (readOnly || !date || !startTime || !endTime) return;
    try {
      if (isManagerMode) {
        if (!instructorId || !managerId) return;
        await axiosInstance.post('/proposal', {
          instructorId,
          managerId,
          date,
          startTime: startTime.length <= 5 ? `${startTime}:00` : startTime,
          endTime: endTime.length <= 5 ? `${endTime}:00` : endTime,
          isRecurring: false,
          rrule: null,
        });
      } else if (isInstructorMode && token) {
        await axios.post(`${baseURL}/proposal/public`, {
          date,
          startTime: startTime.length <= 5 ? `${startTime}:00` : startTime,
          endTime: endTime.length <= 5 ? `${endTime}:00` : endTime,
          isRecurring: false,
          rrule: null,
        }, { params: { token } });
      } else {
        return;
      }

      if (currentRange.start && currentRange.end) {
        fetchEvents(currentRange.start, currentRange.end);
      }
    } catch (err) {
      console.error('Create dragged target slot failed:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to create slot';
      setError(msg);
      // eslint-disable-next-line no-alert
      alert(msg);
    }
  }, [readOnly, isManagerMode, isInstructorMode, instructorId, managerId, token, currentRange.start, currentRange.end, fetchEvents]);

  const handleEventDragStart = useCallback((arg) => {
    const { event } = arg;
    if (readOnly) return;
    const ext = event.extendedProps || {};
    const isManagerTarget = isManagerMode && !ext.isSchedule && ext.sourceType === 'MANAGER';
    const isInstructorAvailable =
      isInstructorMode && !isManagerMode && !ext.isSchedule && ext.sourceType === 'INSTRUCTOR' && !ext.isRejected;
    if (!isManagerTarget && !isInstructorAvailable) return;

    const shadowId = `drag-shadow-${event.id}`;
    dragShadowIdRef.current = shadowId;

    setEvents((prev) => {
      // Remove any previous shadow first
      const withoutOldShadow = prev.filter((e) => !(e.extendedProps && e.extendedProps.isShadow));
      const shadowEvent = {
        id: shadowId,
        title: event.title,
        start: event.start,
        end: event.end,
        backgroundColor: event.backgroundColor,
        extendedProps: {
          ...(event.extendedProps || {}),
          isShadow: true,
        },
      };
      return [...withoutOldShadow, shadowEvent];
    });
  }, [isManagerMode, isInstructorMode, readOnly]);

  const clearDragShadow = useCallback(() => {
    dragShadowIdRef.current = null;
    setEvents((prev) => prev.filter((e) => !(e.extendedProps && e.extendedProps.isShadow)));
  }, []);

  const managerDayCellClassNames = useCallback(
    (arg) => {
      if (!isManagerMode || readOnly) return [];
      const ymd = formatLocalYmd(arg.date);
      return isDateBeforeEarliestManagerNewTargetPst(ymd) ? ['fc-manager-target-window-blocked'] : [];
    },
    [isManagerMode, readOnly],
  );

  const handleEventDrop = useCallback((arg) => {
    const { event, oldEvent } = arg;
    // Always revert visual drag – we only create a copy
    if (arg.revert) {
      arg.revert();
    }
    clearDragShadow();

    if (readOnly) return;
    const ext = event.extendedProps || {};
    const isManagerTarget = isManagerMode && !ext.isSchedule && ext.sourceType === 'MANAGER';
    const isInstructorAvailable =
      isInstructorMode && !isManagerMode && !ext.isSchedule && ext.sourceType === 'INSTRUCTOR' && !ext.isRejected;
    if (!isManagerTarget && !isInstructorAvailable) return;

    const targetStart = event.start;
    if (!targetStart) return;
    const newDateStr = formatLocalYmd(targetStart);

    if (isManagerMode && isDateBeforeEarliestManagerNewTargetPst(newDateStr)) {
      // eslint-disable-next-line no-alert
      alert(
        `Cannot place a new manager target on this date. Earliest allowed is ${getEarliestManagerNewTargetDateYmdPst()} (PST: today + 7 days).`,
      );
      return;
    }

    const originalDateStr =
      ext.date ||
      (oldEvent && oldEvent.start ? formatLocalYmd(oldEvent.start) : null);
    if (!originalDateStr || originalDateStr === newDateStr) return;

    const startTime = getStartTimeStr(event);
    const endTime = getEndTimeStr(event);
    if (!startTime || !endTime || startTime === '--:--' || endTime === '--:--') return;

    createDraggedTargetSlot({ date: newDateStr, startTime, endTime });
  }, [isManagerMode, isInstructorMode, readOnly, createDraggedTargetSlot, getStartTimeStr, getEndTimeStr, clearDragShadow]);

  const handleEventDragStop = useCallback(() => {
    clearDragShadow();
  }, [clearDragShadow]);

  if (!isManagerMode && !isInstructorMode) {
    return <div>Missing instructorId+managerId or token</div>;
  }

  return (
    <div ref={rootRef}>
      {isManagerMode && !readOnly && (
        <style>
          {`
            .fc .fc-daygrid-day.fc-manager-target-window-blocked {
              background-color: rgba(0, 0, 0, 0.075) !important;
            }
            .fc .fc-daygrid-day.fc-day-today.fc-manager-target-window-blocked {
              background-color: rgba(255, 243, 224, 0.98) !important;
              box-shadow: inset 0 0 0 2px rgba(25, 118, 210, 0.5);
            }
          `}
        </style>
      )}
      {loading && <div style={{ padding: 8, color: '#666' }}>Loading...</div>}
      {error && <div style={{ padding: 8, color: '#d32f2f' }}>{error}</div>}
      {(isManagerMode || isInstructorMode) && !isMobile && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 12, backgroundColor: 'rgba(76, 175, 80, 0.5)', borderRadius: 2 }} />
            Schedule (confirmed)
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 12, backgroundColor: 'rgba(33, 150, 243, 0.5)', borderRadius: 2 }} />
            Manager proposal
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 12, backgroundColor: 'rgba(255, 193, 7, 0.5)', borderRadius: 2 }} />
            Instructor proposal
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 12, backgroundColor: 'rgba(244, 67, 54, 0.5)', borderRadius: 2 }} />
            Rejected
          </span>
          {isManagerMode && !readOnly && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, maxWidth: 420 }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: 'rgba(0, 0, 0, 0.075)',
                  border: '1px solid #ccc',
                  borderRadius: 2,
                }}
              />
              Shaded days (PST): no new manager target (before today + 7 days)
            </span>
          )}
        </div>
      )}
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        initialDate={fullCalendarInitialDate}
        validRange={fullCalendarValidRange}
        headerToolbar={headerToolbar}
        customButtons={{
          refresh: {
            text: '⟳',
            click: () => {
              if (onRefresh) onRefresh();
            },
          },
        }}
        events={events}
        eventContent={renderEventContent}
        datesSet={handleDatesSet}
        dateClick={handleDateClick}
        dayCellClassNames={isManagerMode && !readOnly ? managerDayCellClassNames : undefined}
        eventClick={handleEventClick}
        eventDragStart={handleEventDragStart}
        eventDragStop={handleEventDragStop}
        editable={!readOnly && (isManagerMode || isInstructorMode)}
        eventDrop={handleEventDrop}
        height="auto"
        eventMaxStack={10}
        slotEventOverlap={true}
      />
      <ProposalDayView
        open={dayViewOpen}
        onClose={() => setDayViewOpen(false)}
        date={selectedDate}
        events={events}
        isManagerMode={isManagerMode}
        isInstructorMode={isInstructorMode}
        onSlotClick={handleDayViewSlotClick}
        onAddSlot={
          readOnly
            ? undefined
            : () => {
                const rs = rangeRef.current?.start || currentRange.start;
                const re = rangeRef.current?.end || currentRange.end;
                if (rs && re) fetchEvents(rs, re);
              }
        }
        instructorId={instructorId}
        managerId={managerId}
        token={token}
        readOnly={readOnly}
      />
      <ProposalSlotDetailDialog
        open={slotDetailOpen}
        onClose={() => setSlotDetailOpen(false)}
        slot={selectedSlot}
        onSave={
          readOnly
            ? undefined
            : () => {
                const rs = rangeRef.current?.start || currentRange.start;
                const re = rangeRef.current?.end || currentRange.end;
                if (rs && re) fetchEvents(rs, re);
              }
        }
        isManagerMode={isManagerMode}
        instructorId={instructorId}
        managerId={managerId}
        token={token}
      />
    </div>
  );
};

export default ProposalCalendar;
